import * as React from "react";
import Modal from "../../../../ui/modal/Modal";
import Row from "../../../../layout/Row";
import Col from "../../../../layout/Col";
import SectionView from "../../../../layout/section/SectionView";
import ButtonPrimary from "../../../../../hiberUiKit/src/atoms/Buttons/ButtonPrimary";
import {option, Option} from "ts-option";
import {Dispatch} from "redux";
import {connect} from "react-redux";
import BranchLocationDetailsTile from "./BranchLocationDetailsTile";
import "./merchantBranchSelection.scss";
import OpeningHoursBox from "./OpeningHoursBox";
import ButtonSecondary from "../../../../../hiberUiKit/src/atoms/Buttons/ButtonSecondary";
import BranchLocationDropdown from "./BranchLocationDropdown";
import {api} from "../../../../../api/api";
import {notifyBugsnagAndShowToast} from "../../../../../utils/Utils";
import MerchantBranchLocation from "../../../../../model/merchant/MerchantBranchLocation";
import {RootState} from "../../../../../store/reducer/root/RootState";
import {SitesMap} from "../../../../../store/reducer/site/SiteState";
import {fetchSites} from "../../../../../store/action/site/site";
import PartnerBrand from "../../../../../model/partnerBrand/PartnerBrand";
import Toast from "../../../../helper/Toast";
import Loading from "../../../../ui/loading/Loading";
import {OnFailure, OnSuccess} from "../../../../../store/action/actionHelpers";
import {setMerchantBranchLocation} from "../../../../../store/action/billOfMaterials/billOfMaterials";
import {Bom} from "../../../../../model/job/Bom";

interface IComponentProps {
	/**
	 * On cancel action
	 */
	onCancel: () => void;
	/**
	 * Bill of materials merchant name
	 */
	merchantName: string;
	/**
	 * Customer id
	 */
	customerId: string;
	/**
	 * Bill of materials id
	 */
	bom: Bom
	/**
	 * Merchant branch locations
	 */
	merchantBranchLocations: MerchantBranchLocation[]
	/**
	 * Job identifier
	 */
	jobId: string
}

interface ReduxProps {
	/**
	 * Sites
	 */
	sites: SitesMap,
	/**
	 * All sites fetched
	 */
	allSitesFetched: boolean
	/**
	 * Partner brand
	 */
	partnerBrand: Option<PartnerBrand>
}

interface DispatchProps {
	fetchSites: () => void
	setMerchantBranchLocation: (bomId: string, siteId: string, merchantBranchLocationId: string, onSuccess: OnSuccess,
		onFailure: OnFailure) => void
}

interface IState {
	/**
	 * Selected location
	 */
	selectedLocation: Option<MerchantBranchLocation>
}

type Props = IComponentProps & ReduxProps & DispatchProps;

class MerchantBranchSelectionModal extends React.Component<Props, IState> {
	constructor(props: Props) {
		super(props);
		this.state = {
			selectedLocation: option(
				props.merchantBranchLocations.filter(mbl => mbl.id == props.bom.merchantBranchLocation.orNull)[0])
		};
	}

	componentDidMount() {
		if (!this.props.allSitesFetched) {
			this.props.fetchSites()
		}
	}

	componentWillReceiveProps(nextProps: Readonly<Props>, nextContext: any) {
		if (this.props.bom.merchantBranchLocation.orNull != nextProps.bom.merchantBranchLocation.orNull) {
			this.setState({
				selectedLocation: option(
					nextProps.merchantBranchLocations.filter(mbl => mbl.id == nextProps.bom.merchantBranchLocation.orNull)[0])
			})
		}
	}

	onLocationSelect = (location: MerchantBranchLocation) => {
		this.setState({selectedLocation: option(location)});
	};

	setBranchAsPreferred = () => {
		this.props.partnerBrand.forEach(pb => {
			const preferredMerchantBranchLocationId = this.state.selectedLocation.map(sl => sl.id);
			api.instance.partnerBrandApi.updatePartnerBrand(pb.id,
				pb.copy({preferredMerchantBranchLocationId}))
				.then(() => Toast.success("Branch has been set as preferred.", "Set branch as preferred."))
				.catch((err) => notifyBugsnagAndShowToast(err, "Set branch as preferred."));
		})

	};

	setMerchantBranchLocation = () => {
		const merchantBranchLocationId = this.state.selectedLocation.map(sl => sl.id);
		this.props.setMerchantBranchLocation(this.props.bom.id, merchantBranchLocationId.getOrElse(""),
			this.props.jobId,
			() => {
				Toast.success("Merchant branch location has been set.", "Set merchant branch location.");
				this.props.onCancel();
			}, (err) => notifyBugsnagAndShowToast(err, "Set merchant branch location."));
	};

	render() {
		const {selectedLocation} = this.state;
		const site = option(this.props.sites[this.state.selectedLocation.map(sl => sl.siteId).orNull])
		return (
			<Modal mediumWidth onCloseClicked={this.props.onCancel} title={`Choose a ${this.props.merchantName} branch`}>
				<SectionView>
					<div style={{marginTop: '25px', height: '300px'}}>
						<div className="dropdown-wrapper">
							<BranchLocationDropdown
								locations={this.props.merchantBranchLocations}
								onSelect={(location) => this.onLocationSelect(location)}
								value={this.state.selectedLocation}
								placeholder="Choose a branch.."
								sites={this.props.sites}
								partnerBrand={this.props.partnerBrand}
							/>
						</div>
						{selectedLocation.isDefined &&
						<Row>
							<Col xl={4}>
								<div className="address-details-wrapper">
									{site.map(s => <BranchLocationDetailsTile title="Address"
																			  body={`${s.data.address.displayStreet()} ${s.data.address.geocodingAddress()}`}/>).getOrElse(
										<Loading isLoading={true} spinnerSize={24}/>)
									}
									{/* TODO: Uncomment and add branch manager when backend ready
										<BranchLocationDetailsTile title="Branch Manager" body={selectedLocation.map(sl => sl.branchManager).getOrElse("")} />*/}
									{selectedLocation.map(sl => sl.phone).isDefined &&
									<BranchLocationDetailsTile title="Phone number"
															   body={selectedLocation.map(sl => sl.phone).getOrElse("")}/>
									}
								</div>
							</Col>
							<Col xl={8}>
								{selectedLocation.flatMap(sl => sl.openingHours.map(
									oh => <OpeningHoursBox title="Opening hours" openingHours={oh}/>)).orNull}
							</Col>
						</Row>
						}
					</div>
				</SectionView>
				<div className="buttons-wrapper">
					<div>
						{this.props.partnerBrand && this.state.selectedLocation.isDefined &&
						<ButtonSecondary label="Set as preferred" onClick={this.setBranchAsPreferred}/>
						}
					</div>
					<ButtonPrimary onClick={this.setMerchantBranchLocation}
								   label={"Done"}
								   disabled={this.state.selectedLocation.isEmpty}/>
				</div>
			</Modal>
		);
	}
}

function mapStateToProps(state: RootState, props: IComponentProps) {
	const sites = state.site.sitesMap;
	const allSitesFetched = state.site.allSitesFetched;
	const contact = option(state.contact.individualContactMap[props.customerId]);
	const partnerBrandId = contact.flatMap(c => c.data.partnerBrandId).orNull;
	const partnerBrand = option(state.partnerBrand.partnerBrandsMap[partnerBrandId]);
	return {
		sites,
		partnerBrand,
		allSitesFetched
	}
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
	return {
		fetchSites: () => dispatch(fetchSites(() => {}, () => {})),
		setMerchantBranchLocation: (bomId: string, merchantBranchLocationId: string, jobId: string, onSuccess: OnSuccess,
			onFailure: OnFailure) => dispatch(
			setMerchantBranchLocation(bomId, merchantBranchLocationId, jobId, onSuccess, onFailure))
	}
}

export default connect(mapStateToProps, mapDispatchToProps)(MerchantBranchSelectionModal);